import * as api from './julysnow-checkout-20260916-api.js';
import { runShopifyCheckout } from './julysnow-checkout-20260916-checkout-flow.js';
import { createNativeCheckout, POST_PAYMENT_NOTICE } from './julysnow-checkout-currency-20260917-native-checkout.js';
import { readThemeConfig, resolvePublicAssetUrl } from './julysnow-checkout-20260916-theme-config.js';
import { catalogDateText, catalogSummaryText, diamondInquiryLinks, inquiryAvailabilityText, partialInventoryNotice, INQUIRY_PRICE_NOTE } from './julysnow-checkout-20260916-inquiry.js';
import {
  buildQuoteInput,
  canReviewSelection,
  checkoutIdempotencyKey,
  clearDiamondState,
  inventoryStatusText,
  isTransientError,
  selectDiamond,
  setPurchaseMode,
  writeSelection,
} from './julysnow-checkout-20260916-state.js';

const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const themeConfig = readThemeConfig();
api.configureApi(themeConfig);
const nativeCheckout = createNativeCheckout({
  enabled: themeConfig.nativeCheckoutEnabled,
  storefrontRoot: themeConfig.storefrontRoot,
  shopCurrency: themeConfig.shopCurrency,
  cartCurrency: themeConfig.cartCurrency,
});
const checkoutPriceNote = themeConfig.nativeCheckoutEnabled
  ? 'Review the payable diamond price before checkout. Taxes and shipping, if applicable, are calculated in Shopify checkout.'
  : INQUIRY_PRICE_NOTE;
let nativeReviewVersion = 0;
let diamondSelectionVersion = 0;
const assetUrl = (key, fallback) => themeConfig.assetUrls[key] ?? fallback;
const EMPTY_DIAMOND_IMAGE = assetUrl(
  'round_placeholder',
  '/assets/diamonds/placeholders/round.webp',
);
const PAGE_SIZE = themeConfig.pageSize;
const TABLE_COLUMNS = 17;
const MAX_COMPARE = 4;
const WHATSAPP_NUMBER = '85266152639';
const WHATSAPP_DISPLAY = '+852 6615 2639';
const EMAIL_ADDRESS = 'support@julysnowjewelry.com';
const LOCATION_LABELS = new Map([
  ['上海', 'Shanghai'],
  ['深圳', 'Shenzhen'],
  ['香港', 'Hong Kong'],
  ['广州', 'Guangzhou'],
  ['北京', 'Beijing'],
]);

function locationLabel(value) {
  const text = String(value ?? '').trim();
  if (!text) return 'Not specified';
  return LOCATION_LABELS.get(text) ?? text;
}

const ui = {
  appMessage: document.querySelector('#app-message'),
  banner: document.querySelector('#data-banner'),
  completeRing: document.querySelector('#complete-ring'),
  completeHeading: document.querySelector('#complete-heading'),
  desktopFilters: document.querySelector('#desktop-filters'),
  diamondDrawer: document.querySelector('#diamond-drawer'),
  diamondMeta: document.querySelector('#diamond-result-meta'),
  diamondResults: document.querySelector('#diamond-results'),
  drawerContent: document.querySelector('#drawer-content'),
  filterDialog: document.querySelector('#filter-dialog'),
  mobileFilters: document.querySelector('#mobile-filters'),
  mobileSort: document.querySelector('#mobile-sort'),
  mobileToolbar: document.querySelector('.mobile-toolbar'),
  pagination: document.querySelector('#pagination'),
  progressItems: [...document.querySelectorAll('[data-progress-step]')],
  startOver: document.querySelector('#start-over'),
  resultCount: document.querySelector('#results-count'),
  compareTray: document.querySelector('#compare-tray'),
  compareSlots: document.querySelector('#compare-slots'),
  compareOpen: document.querySelector('#open-compare'),
  compareClear: document.querySelector('#clear-compare'),
  compareDialog: document.querySelector('#compare-dialog'),
  compareContent: document.querySelector('#compare-content'),
  sort: document.querySelector('#sort-select'),
  stepPanels: [...document.querySelectorAll('.step-panel')],
  summaryDetails: document.querySelector('#summary-details'),
  summaryHeading: document.querySelector('#summary-heading'),
  summaryTotal: document.querySelector('#summary-total'),
  summaryVisual: document.querySelector('#summary-visual'),
};

const model = {
  compare: [],
  diamonds: [],
  filters: {},
  meta: null,
  page: 1,
  quote: null,
  selectedDiamond: null,
  selection: selectDiamond(null, null),
  sort: 'price_asc',
  step: 1,
};

function element(tagName, className, text) {
  const node = document.createElement(tagName);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function icon(name) {
  const node = document.createElement('i');
  node.dataset.lucide = name;
  return node;
}

function refreshIcons() {
  window.lucide?.createIcons({ attrs: { 'aria-hidden': 'true' } });
}

function safeImage(source, alt, className) {
  const image = element('img', className);
  image.src = resolvePublicAssetUrl(source, themeConfig);
  image.alt = alt;
  image.loading = 'lazy';
  return image;
}

function verificationLink(url, className = 'certificate-link', certNo = '') {
  if (!url) return null;
  const isSynthetic = /^FIXTURE-/i.test(String(certNo).trim());
  const link = element('a', className, isSynthetic ? 'Open IGI (test)' : 'Verify on IGI');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  if (isSynthetic) {
    link.title = 'Test certificate number; IGI may not return a report.';
    link.setAttribute('aria-label', 'Open IGI test certificate link');
  }
  return link;
}

function inquiryLinks(diamond = null) {
  return diamondInquiryLinks(diamond, model.meta?.synced_at, {
    whatsappNumber: WHATSAPP_NUMBER,
    emailAddress: EMAIL_ADDRESS,
  });
}

function availabilityText(diamond) {
  return themeConfig.inquiryMode ? inquiryAvailabilityText(diamond) : inventoryStatusText(diamond);
}

function whatsappUrl(diamond = null) {
  if (themeConfig.inquiryMode) return inquiryLinks(diamond).whatsapp;
  const message = diamond
    ? `Hi Julysnow, could you share additional photos or a video for diamond ${diamond.cert_no} (${diamond.carat.toFixed(2)} ct ${diamond.shape}, ${diamond.color}/${diamond.clarity})?`
    : 'Hi Julysnow, could you share additional photos or a video of a loose diamond I am considering?';
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

function emailUrl(diamond = null) {
  if (themeConfig.inquiryMode) return inquiryLinks(diamond).email;
  const subject = diamond
    ? `Diamond media request - ${diamond.cert_no}`
    : 'Diamond photos or video request';
  const body = diamond
    ? `Hi Julysnow,\n\nCould you share additional photos or a video for diamond ${diamond.cert_no} (${diamond.carat.toFixed(2)} ct ${diamond.shape}, ${diamond.color}/${diamond.clarity})?\n\nThank you.`
    : 'Hi Julysnow,\n\nCould you share additional photos or a video of a loose diamond I am considering?\n\nThank you.';
  return `mailto:${EMAIL_ADDRESS}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function updateWhatsAppHelp(diamond = null) {
  const link = document.querySelector('#whatsapp-help');
  if (!link) return;
  link.href = whatsappUrl(diamond);
  link.setAttribute(
    'aria-label',
    themeConfig.inquiryMode
      ? 'Check diamond availability and final price with Julysnow on WhatsApp'
      : diamond
      ? `Ask Julysnow on WhatsApp for additional photos or video of ${diamond.carat.toFixed(2)} carat ${diamond.shape} diamond`
      : `Ask Julysnow on WhatsApp for additional diamond photos or video at ${WHATSAPP_DISPLAY}`,
  );
}

function updateEmailHelp(diamond = null) {
  const link = document.querySelector('#email-help');
  if (!link) return;
  link.href = emailUrl(diamond);
  link.setAttribute(
    'aria-label',
    themeConfig.inquiryMode
      ? 'Email Julysnow to confirm diamond availability and final price'
      : diamond
      ? `Email Julysnow for additional photos or video of ${diamond.carat.toFixed(2)} carat ${diamond.shape} diamond`
      : `Email Julysnow at ${EMAIL_ADDRESS} for additional diamond photos or video`,
  );
}

function gradedValue(value, suffix = '') {
  return value === null || value === undefined || value === ''
    ? 'Not provided'
    : `${value}${suffix}`;
}

function showMessage(message) {
  ui.appMessage.textContent = message;
  ui.appMessage.hidden = !message;
}

function errorMessage(error) {
  if (isTransientError(error)) {
    return themeConfig.inquiryMode
      ? 'The catalog is temporarily unavailable. Your selection has been kept for retry.'
      : 'Inventory is temporarily unavailable. Your selection has been kept for retry.';
  }
  return error?.message || 'The request could not be completed. Please try again.';
}

function reviewSelectionContext() {
  return {
    selection: model.selection,
    quote: model.quote,
    selectedDiamond: model.selectedDiamond,
  };
}

function canReviewCurrentSelection() {
  return canReviewSelection(reviewSelectionContext());
}

function fallbackDiamondLabel() {
  return model.selection.diamond_id
    ? `Diamond ${model.selection.diamond_id}`
    : 'Not selected';
}

function updateBanner(meta) {
  ui.banner.classList.toggle('is-stale', Boolean(meta?.is_stale || meta?.stale));
  if (!meta) {
    ui.banner.hidden = true;
    return;
  }
  if (themeConfig.nativeCheckoutEnabled && meta.data_mode !== 'fixture') {
    const freshness = meta.partial_update_count && meta.latest_partial_update_at
      ? `${meta.partial_update_count.toLocaleString('en-US')} diamonds checked ${catalogDateText(meta.latest_partial_update_at)}. Other entries are reference data dated ${catalogDateText(meta.last_full_snapshot_at || meta.synced_at)}.`
      : `Catalog reference data dated ${catalogDateText(meta.synced_at)}.`;
    ui.banner.textContent = freshness + ' Availability and inspection are confirmed manually after payment.';
    ui.banner.hidden = false;
    return;
  }
  const synced = new Date(meta.synced_at).toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  if (meta.data_mode === 'fixture') {
    ui.banner.textContent = `Fixture data - not live inventory. Synthetic snapshot: ${synced}.`;
    ui.banner.hidden = false;
    return;
  }
  if (meta.data_mode === 'historical' && partialInventoryNotice(meta)) {
    ui.banner.textContent = partialInventoryNotice(meta);
    ui.banner.hidden = false;
    return;
  }
  if (meta.data_mode === 'historical') {
    ui.banner.textContent = themeConfig.inquiryMode
      ? `Historical inventory snapshot - not live availability. Catalog date: ${catalogDateText(meta.synced_at)}. Prices are for reference; contact us to confirm availability and final price.`
      : `Historical inventory snapshot - not live availability. Snapshot: ${synced}. Checkout is disabled.`;
    ui.banner.hidden = false;
    return;
  }
  if (themeConfig.inquiryMode) {
    ui.banner.textContent = `Catalog date: ${catalogDateText(meta.synced_at)}. Availability, prices and delivery time require confirmation before payment.`;
    ui.banner.hidden = false;
    return;
  }
  if (meta.is_stale) {
    ui.banner.textContent = `Inventory snapshot is over 24 hours old. Last synced: ${synced}.`;
    ui.banner.hidden = false;
    return;
  }
  ui.banner.hidden = true;
}

function updateShapeAvailability(meta) {
  if (!Array.isArray(meta?.available_shapes)) return;
  const availableShapes = new Set(meta.available_shapes);
  for (const button of document.querySelectorAll('.shape-button[data-shape]')) {
    const shape = button.dataset.shape;
    if (!shape) continue;
    const unavailable = !availableShapes.has(shape);
    button.disabled = unavailable;
    button.setAttribute('aria-disabled', String(unavailable));
    if (unavailable) {
      button.title = `${shape}: No diamonds in this snapshot`;
      button.dataset.availability = themeConfig.inquiryMode ? '0 listed' : '0 available';
    } else {
      button.title = shape;
      delete button.dataset.availability;
    }
  }
}

function renderDiamondSkeleton() {
  ui.diamondResults.replaceChildren();
  for (let rowIndex = 0; rowIndex < 6; rowIndex += 1) {
    const row = element('tr', 'skeleton-row');
    for (let columnIndex = 0; columnIndex < TABLE_COLUMNS; columnIndex += 1) {
      const cell = element('td');
      cell.append(element('span', 'skeleton-block'));
      row.append(cell);
    }
    ui.diamondResults.append(row);
  }
}

function detailButton(diamond) {
  const button = element('button', 'diamond-image-button');
  button.type = 'button';
  button.title = `View ${diamond.shape} diamond details`;
  button.setAttribute('aria-label', `View ${diamond.shape} diamond details`);
  button.append(safeImage(
    diamond.image_url,
    `${diamond.shape} ${diamond.carat} carat diamond`,
  ));
  if (diamond.video_url) {
    const mediaBadge = element('span', 'media-badge', 'Video');
    mediaBadge.setAttribute('aria-hidden', 'true');
    mediaBadge.prepend(icon('play'));
    button.append(mediaBadge);
  }
  button.addEventListener('click', () => openDiamondDrawer(diamond));
  return button;
}

function tableCell(text, className) {
  return element('td', className, text);
}

function certificateTableCell(diamond) {
  const cell = element('td');
  const link = verificationLink(diamond.certificate_url, 'table-certificate-link', diamond.cert_no);
  if (!link) {
    cell.textContent = diamond.lab;
    return cell;
  }
  link.textContent = diamond.lab;
  link.title = `Verify ${diamond.cert_no} on IGI`;
  link.setAttribute('aria-label', `Verify ${diamond.cert_no} on IGI`);
  cell.append(link);
  return cell;
}

function numericCell(value, digits, suffix = '') {
  return tableCell(
    value === null || value === undefined
      ? '—'
      : `${Number(value).toFixed(digits)}${suffix}`,
    'table-numeric',
  );
}

function compareCell(diamond) {
  const cell = element('td', 'compare-cell');
  const label = element('label', 'compare-checkbox');
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.checked = model.compare.some((item) => item.diamond_id === diamond.diamond_id);
  input.disabled = !input.checked && model.compare.length >= MAX_COMPARE;
  input.setAttribute(
    'aria-label',
    `Compare ${diamond.carat.toFixed(2)} carat ${diamond.shape} diamond`,
  );
  input.addEventListener('change', () => toggleCompare(diamond, input.checked));
  label.append(input);
  cell.append(label);
  return cell;
}

function renderDiamonds() {
  ui.diamondResults.replaceChildren();
  if (model.diamonds.length === 0) {
    const row = element('tr', 'empty-result-row');
    const cell = tableCell('', 'empty-result-cell');
    const edit = element('button', 'button button-primary', 'Edit filters');
    edit.type = 'button';
    edit.addEventListener('click', () => document.querySelector('#open-filters').click());
    cell.append(
      element('h3', null, 'No diamonds match these filters.'),
      element('p', null, 'Try a wider carat range or budget to see more options.'),
      edit,
    );
    cell.colSpan = TABLE_COLUMNS;
    row.append(cell);
    ui.diamondResults.append(row);
    return;
  }

  for (const diamond of model.diamonds) {
    const row = element('tr');
    const imageCell = element('td');
    imageCell.append(detailButton(diamond));
    const colorCell = tableCell(diamond.color);
    colorCell.dataset.secondary = diamond.clarity;
    const actionCell = element('td');
    const choose = element('button', 'button button-secondary row-action', 'Select');
    choose.type = 'button';
    choose.addEventListener('click', () => chooseDiamond(diamond));
    actionCell.append(choose);
    row.append(
      compareCell(diamond),
      imageCell,
      tableCell(diamond.shape),
      tableCell(diamond.carat.toFixed(2), 'table-numeric'),
      colorCell,
      tableCell(diamond.clarity),
      tableCell(shortGrade(diamond.cut)),
      tableCell(shortGrade(diamond.polish)),
      tableCell(shortGrade(diamond.symmetry)),
      tableCell(shortFluorescence(diamond.fluorescence)),
      numericCell(diamond.lw_ratio, 2),
      numericCell(diamond.table_pct, 1, '%'),
      numericCell(diamond.depth_pct, 1, '%'),
      certificateTableCell(diamond),
      tableCell(availabilityText(diamond)),
      tableCell(money.format(diamond.price_usd), 'table-price'),
      actionCell,
    );
    ui.diamondResults.append(row);
  }
}

const SHORT_GRADES = new Map([
  ['Ideal', 'ID'],
  ['Excellent', 'EX'],
  ['Very Good', 'VG'],
  ['Good', 'GD'],
]);
const SHORT_FLUORESCENCE = new Map([
  ['None', 'NON'],
  ['Faint', 'FNT'],
  ['Medium', 'MED'],
  ['Strong', 'STG'],
  ['Very Strong', 'VST'],
]);

function shortGrade(value) {
  return value ? (SHORT_GRADES.get(value) ?? value) : '—';
}

function shortFluorescence(value) {
  return value ? (SHORT_FLUORESCENCE.get(value) ?? value) : '—';
}

function toggleCompare(diamond, checked) {
  const without = model.compare.filter((item) => item.diamond_id !== diamond.diamond_id);
  if (checked && without.length >= MAX_COMPARE) {
    showMessage(`You can compare up to ${MAX_COMPARE} diamonds at a time.`);
    renderDiamonds();
    return;
  }
  model.compare = checked ? [...without, diamond] : without;
  showMessage('');
  renderCompareTray();
  renderDiamonds();
}

function renderCompareTray() {
  const count = model.compare.length;
  ui.compareTray.hidden = count === 0;
  ui.compareOpen.textContent = `Compare (${count})`;
  ui.compareOpen.disabled = count < 2;
  ui.compareSlots.replaceChildren();

  for (const diamond of model.compare) {
    const slot = element('div', 'compare-slot');
    slot.append(safeImage(
      diamond.image_url,
      `${diamond.shape} ${diamond.carat.toFixed(2)} carat diamond`,
    ));
    const caption = element('div', 'compare-slot-copy');
    caption.append(
      element('strong', null, `${diamond.carat.toFixed(2)} ct ${diamond.shape}`),
      element('span', null, `${diamond.color} · ${diamond.clarity}`),
    );
    const remove = element('button', 'icon-button compare-remove');
    remove.type = 'button';
    remove.title = 'Remove from comparison';
    remove.setAttribute('aria-label', 'Remove from comparison');
    remove.append(icon('x'));
    remove.addEventListener('click', () => toggleCompare(diamond, false));
    slot.append(caption, remove);
    ui.compareSlots.append(slot);
  }
  refreshIcons();
}

const COMPARE_ROWS = [
  ['Shape', (diamond) => diamond.shape],
  ['Carat', (diamond) => diamond.carat.toFixed(2)],
  ['Color', (diamond) => diamond.color],
  ['Clarity', (diamond) => diamond.clarity],
  ['Cut', (diamond) => gradedValue(diamond.cut)],
  ['Polish', (diamond) => gradedValue(diamond.polish)],
  ['Symmetry', (diamond) => gradedValue(diamond.symmetry)],
  ['Fluorescence', (diamond) => gradedValue(diamond.fluorescence)],
  ['Measurements', (diamond) => diamond.measurements],
  ['L/W ratio', (diamond) => (diamond.lw_ratio === null ? '—' : diamond.lw_ratio.toFixed(2))],
  ['Table', (diamond) => gradedValue(diamond.table_pct, '%')],
  ['Depth', (diamond) => gradedValue(diamond.depth_pct, '%')],
  ['Lab', (diamond) => diamond.lab],
  ['Report', (diamond) => diamond.cert_no],
  ['Availability', (diamond) => availabilityText(diamond)],
  [themeConfig.inquiryMode ? 'Reference price (USD)' : 'Price', (diamond) => money.format(diamond.price_usd)],
];

function openCompareDialog() {
  if (model.compare.length < 2) return;
  ui.compareContent.replaceChildren();

  const table = element('table', 'compare-table');
  const head = element('thead');
  const headRow = element('tr');
  headRow.append(element('th', null, ''));
  for (const diamond of model.compare) {
    const cell = element('th');
    cell.scope = 'col';
    cell.append(safeImage(
      diamond.image_url,
      `${diamond.shape} ${diamond.carat.toFixed(2)} carat diamond`,
      'compare-head-image',
    ));
    cell.append(element('span', null, `${diamond.carat.toFixed(2)} ct ${diamond.shape}`));
    headRow.append(cell);
  }
  head.append(headRow);

  const body = element('tbody');
  for (const [label, read] of COMPARE_ROWS) {
    const row = element('tr');
    const header = element('th', null, label);
    header.scope = 'row';
    row.append(header);
    for (const diamond of model.compare) {
      row.append(element('td', null, String(read(diamond))));
    }
    body.append(row);
  }

  const actions = element('tr');
  actions.append(element('th', null, ''));
  for (const diamond of model.compare) {
    const cell = element('td');
    const choose = element('button', 'button button-primary', 'Select');
    choose.type = 'button';
    choose.addEventListener('click', () => {
      ui.compareDialog.close();
      chooseDiamond(diamond);
    });
    cell.append(choose);
    actions.append(cell);
  }
  body.append(actions);

  table.append(head, body);
  ui.compareContent.append(table);
  ui.compareDialog.showModal();
}

function renderPagination() {
  ui.pagination.replaceChildren();
  const totalPages = Math.max(1, Math.ceil((model.meta?.total ?? 0) / PAGE_SIZE));
  if (totalPages <= 1) return;

  const previous = element('button');
  previous.type = 'button';
  previous.title = 'Previous page';
  previous.setAttribute('aria-label', 'Previous page');
  previous.disabled = model.page <= 1;
  previous.append(icon('chevron-left'));
  previous.addEventListener('click', () => changePage(model.page - 1));
  ui.pagination.append(previous);

  const start = Math.max(1, Math.min(model.page - 2, totalPages - 4));
  const end = Math.min(totalPages, start + 4);
  for (let page = start; page <= end; page += 1) {
    const button = element('button', null, String(page));
    button.type = 'button';
    if (page === model.page) button.setAttribute('aria-current', 'page');
    button.addEventListener('click', () => changePage(page));
    ui.pagination.append(button);
  }

  const next = element('button');
  next.type = 'button';
  next.title = 'Next page';
  next.setAttribute('aria-label', 'Next page');
  next.disabled = model.page >= totalPages;
  next.append(icon('chevron-right'));
  next.addEventListener('click', () => changePage(model.page + 1));
  ui.pagination.append(next);
  refreshIcons();
}

async function loadDiamonds() {
  showMessage('');
  renderDiamondSkeleton();
  ui.diamondMeta.textContent = 'Loading inventory';
  try {
    const response = await api.getDiamonds({
      ...model.filters,
      page: model.page,
      page_size: PAGE_SIZE,
      sort: model.sort,
    });
    model.diamonds = response.data;
    model.meta = response.meta;
    const selected = response.data.find((diamond) => (
      diamond.diamond_id === model.selection.diamond_id
    ));
    if (selected) model.selectedDiamond = selected;
    const synced = new Date(response.meta.synced_at).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
    ui.diamondMeta.textContent = themeConfig.inquiryMode
      ? catalogSummaryText(response.meta)
      : `Snapshot ${synced}`;
    ui.resultCount.textContent = resultCountText(response.data.length, response.meta.total);
    updateBanner(response.meta);
    updateShapeAvailability(response.meta);
    renderDiamonds();
    renderCompareTray();
    renderPagination();
    renderSummary();
  } catch (error) {
    model.diamonds = [];
    ui.diamondMeta.textContent = themeConfig.inquiryMode ? 'Catalog unavailable' : 'Inventory unavailable';
    ui.resultCount.textContent = 'Showing lab diamonds (0 of 0)';
    renderDiamonds();
    showMessage(errorMessage(error));
  }
}

function resultCountText(shown, total) {
  if (total === 0) return 'Showing lab diamonds (0 of 0)';
  const first = (model.page - 1) * PAGE_SIZE + 1;
  const last = first + shown - 1;
  const range = shown === 1 ? `${first}` : `${first}-${last}`;
  return `Showing lab diamonds (${range} of ${total.toLocaleString('en-US')})`;
}

function changePage(page) {
  if (page < 1 || page === model.page) return;
  model.page = page;
  loadDiamonds();
  document.querySelector('#diamond-heading').scrollIntoView({ block: 'start' });
}

async function chooseDiamond(diamond) {
  const selectionVersion = ++diamondSelectionVersion;
  model.selection = selectDiamond(
    setPurchaseMode(model.selection, 'diamond_only'),
    diamond.diamond_id,
  );
  model.selectedDiamond = diamond;
  model.quote = null;
  writeSelection(model.selection);
  ui.diamondDrawer.close();
  showMessage(themeConfig.inquiryMode ? 'Loading your selected diamond...' : 'Checking diamond and price...');
  try {
    const quote = await api.postQuote(buildQuoteInput(model.selection));
    if (selectionVersion !== diamondSelectionVersion) return;
    model.quote = quote;
    if (!canReviewCurrentSelection()) {
      model.quote = null;
      showStep(1);
      showMessage(themeConfig.inquiryMode
        ? 'The selected diamond could not be found in this catalog. Please choose it again.'
        : 'The quote could not be verified against current inventory. Choose the diamond again.');
      return;
    }
    showMessage('');
    renderComplete();
    renderSummary();
    showStep(2);
  } catch (error) {
    if (selectionVersion !== diamondSelectionVersion) return;
    if (error.status === 404 || error.status === 409) {
      Object.assign(model, clearDiamondState(model.selection));
      writeSelection(model.selection);
    }
    showStep(1);
    showMessage(errorMessage(error));
  }
}

function renderComplete() {
  nativeReviewVersion += 1;
  ui.completeRing.replaceChildren();
  const review = reviewSelectionContext();
  if (!canReviewSelection(review)) return;
  const { selectedDiamond: diamond } = review;
  ui.completeHeading.textContent = 'Review your loose diamond';
  const visual = element('div', 'complete-visual');
  visual.append(safeImage(
    diamond?.image_url ?? EMPTY_DIAMOND_IMAGE,
    'Selected loose diamond',
  ));

  const details = element('div', 'complete-details');
  details.append(
    element('p', 'step-kicker', 'Loose diamond'),
    element('h2', null, diamond ? `${diamond.carat.toFixed(2)} ct ${diamond.shape} diamond` : 'Your loose diamond'),
    element('p', 'complete-meta', diamond
      ? `${diamond.color} color · ${diamond.clarity} clarity · ${gradedValue(diamond.cut)} cut`
      : 'Selected loose diamond'),
  );
  const certificateDetails = element('dl', 'detail-grid complete-certificate-details');
  if (diamond) {
    certificateDetails.append(
      summaryRow('Certificate lab', diamond.lab),
      summaryRow('Certificate no.', diamond.cert_no),
      summaryRow('Cut', gradedValue(diamond.cut)),
      summaryRow('Polish', gradedValue(diamond.polish)),
      summaryRow('Symmetry', gradedValue(diamond.symmetry)),
      summaryRow('Fluorescence', gradedValue(diamond.fluorescence)),
      summaryRow('Measurements', gradedValue(diamond.measurements)),
      summaryRow('Availability', availabilityText(diamond)),
    );
    details.append(certificateDetails);
  }
  const completeVerifyLink = verificationLink(diamond?.certificate_url, 'certificate-link', diamond?.cert_no);
  if (completeVerifyLink) details.append(completeVerifyLink);
  const prices = element('dl', 'price-lines');
  const priceRows = themeConfig.inquiryMode
    ? [['Reference price (USD)', money.format(model.quote.diamond.price_usd), 'total-line']]
    : [
      ['Diamond', money.format(model.quote.diamond.price_usd), ''],
      ['Estimated total', money.format(model.quote.total_price), 'total-line'],
    ];
  for (const [label, value, className] of priceRows) {
    const line = element('div', className);
    line.append(element('dt', null, label), element('dd', null, value));
    prices.append(line);
  }
  details.append(prices, element('p', 'complete-note', themeConfig.inquiryMode
    ? checkoutPriceNote
    : 'Tax calculated later in Shopify checkout.'));
  if (themeConfig.inquiryMode) {
    details.append(element('p', 'complete-note', `Catalog date: ${catalogDateText(diamond.synced_at || model.meta?.synced_at)}. Sending an inquiry does not reserve this diamond.`));
  }

  const actions = element('div', 'complete-actions');
  if (themeConfig.inquiryMode) {
    if (themeConfig.nativeCheckoutEnabled) renderNativeCheckout(diamond, details);
    const availabilityLink = element('a', themeConfig.nativeCheckoutEnabled ? 'button button-secondary' : 'button button-primary', 'Check availability');
    availabilityLink.href = whatsappUrl(diamond);
    availabilityLink.target = '_blank';
    availabilityLink.rel = 'noopener noreferrer';
    availabilityLink.setAttribute('aria-label', 'Check availability and final price on WhatsApp');
    availabilityLink.prepend(icon('message-circle'));
    const emailLink = element('a', 'button button-secondary', 'Email us');
    emailLink.href = emailUrl(diamond);
    emailLink.prepend(icon('mail'));
    const changeDiamond = element('button', 'button button-secondary', 'Change diamond');
    changeDiamond.type = 'button';
    changeDiamond.addEventListener('click', () => showStep(1));
    actions.append(availabilityLink, emailLink, changeDiamond);
    details.append(element('p', 'complete-note', themeConfig.nativeCheckoutEnabled
      ? 'Prefer to ask first? Contact us on WhatsApp or email for availability, photos and delivery details.'
      : 'Choose WhatsApp or email to send your request. Any shipping charges and taxes will be shown with your confirmed payment details.'), actions);
    ui.completeRing.append(visual, details);
    refreshIcons();
    return;
  }
  const checkoutButton = element('button', 'button button-primary');
  checkoutButton.type = 'button';
  const historicalCheckout = themeConfig.testMode
    && themeConfig.embeddedCatalogUrl
    && !themeConfig.cartEnabled;
  checkoutButton.append(
    icon('shopping-bag'),
    element('span', null, historicalCheckout
      ? 'Checkout unavailable in historical test'
      : 'Continue to secure checkout'),
  );
  checkoutButton.disabled = !themeConfig.cartEnabled;
  if (themeConfig.cartEnabled) checkoutButton.addEventListener('click', startCheckout);
  const diamondButton = element('button', 'button button-secondary', 'Change diamond');
  diamondButton.type = 'button';
  diamondButton.addEventListener('click', () => showStep(1));
  if (historicalCheckout) {
    details.append(element(
      'p',
      'checkout-status-note',
      'Historical snapshot only. Live inventory and checkout are not connected yet.',
    ));
  }
  actions.append(checkoutButton, diamondButton);
  details.append(actions);
  ui.completeRing.append(visual, details);
  refreshIcons();
}

function renderNativeCheckout(diamond, details) {
  const version = nativeReviewVersion;
  const panel = element('section', 'native-checkout-panel');
  panel.setAttribute('aria-label', 'Buy this diamond');
  const title = element('h3', null, 'Buy this diamond');
  const price = element('p', 'native-checkout-price');
  const shipping = element('p', 'complete-note native-checkout-shipping');
  shipping.hidden = true;
  const status = element('p', 'checkout-status-note', 'Loading the Shopify checkout price...');
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  const notice = element('p', 'complete-note', POST_PAYMENT_NOTICE);
  const button = element('button', 'button button-primary', 'Buy now');
  button.type = 'button';
  button.disabled = true;
  const retryButton = element('button', 'button button-secondary', 'Retry checkout check');
  retryButton.type = 'button';
  retryButton.hidden = true;
  const actions = element('div', 'complete-actions');
  actions.append(button, retryButton);
  panel.append(title, price, shipping, status, notice, actions);
  details.append(panel);
  let acceptedReview = null;
  let submitting = false;
  let reviewing = false;
  const isCurrent = () => version === nativeReviewVersion
    && model.step === 2 && model.selectedDiamond?.diamond_id === diamond.diamond_id
    && panel.isConnected;
  const applyReview = (review, changed = false) => {
    acceptedReview = review;
    button.hidden = false;
    retryButton.hidden = true;
    button.disabled = submitting;
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency', currency: review.currency, currencyDisplay: 'code',
    }).format(review.priceCents / 100);
    price.textContent = 'Diamond price at checkout: ' + formatted;
    shipping.textContent = 'Free standard shipping when your order subtotal after discounts, before tax, is $500 USD or more; otherwise $30 USD once per order.'
      + (review.currency !== 'USD'
        ? ' The threshold is based on USD. Shipping is calculated at checkout and shown in your checkout currency.'
        : ' Shipping is calculated at checkout using all items in your order.');
    shipping.hidden = false;
    status.textContent = changed || review.priceChanged
      ? 'Updated Shopify price: ' + formatted + '. Please review this price, then click Buy now to continue.'
      : review.currency !== 'USD'
        ? 'Checkout uses ' + review.currency + '. The reference catalog is in USD.'
        : 'Payment is followed by our manual availability check and inspection.';
  };
  const showFailure = (error) => {
    acceptedReview = null;
    button.disabled = true;
    price.textContent = '';
    shipping.textContent = '';
    shipping.hidden = true;
    status.textContent = error.message || 'Checkout is unavailable. You can still contact us below.';
    const canRetry = ['NOT_OPEN', 'NETWORK', 'REQUEST', 'RESPONSE', 'RATE_LIMIT', 'SERVICE', 'UNAVAILABLE', 'CURRENCY_CHANGED'].includes(error.code);
    retryButton.textContent = ['NOT_OPEN', 'UNAVAILABLE'].includes(error.code)
      ? 'Check again' : 'Retry checkout check';
    retryButton.hidden = !canRetry;
    retryButton.disabled = false;
    button.hidden = canRetry;
  };
  const loadReview = async () => {
    if (reviewing || submitting) return;
    reviewing = true;
    acceptedReview = null;
    button.disabled = true;
    retryButton.disabled = true;
    price.textContent = '';
    shipping.textContent = '';
    shipping.hidden = true;
    status.textContent = 'Checking the Shopify checkout price and purchase availability...';
    try {
      const review = await nativeCheckout.review(diamond);
      if (isCurrent()) applyReview(review);
    } catch (error) {
      if (isCurrent()) showFailure(error);
    } finally {
      reviewing = false;
      if (isCurrent()) retryButton.disabled = false;
    }
  };
  retryButton.addEventListener('click', () => {
    if (isCurrent()) void loadReview();
  });
  button.addEventListener('click', async () => {
    if (reviewing || submitting || !acceptedReview || !isCurrent()) return;
    submitting = true;
    button.disabled = true;
    button.textContent = 'Preparing secure checkout...';
    status.textContent = 'Rechecking the Shopify price and preparing your cart...';
    let redirected = false;
    try {
      await nativeCheckout.checkout({ diamond, review: acceptedReview, isCurrent });
      redirected = true;
    } catch (error) {
      if (!isCurrent()) return;
      if (error.code === 'PRICE_CHANGED' && error.review) applyReview(error.review, true);
      else showFailure(error);
    } finally {
      submitting = false;
      if (isCurrent() && !redirected) {
        button.textContent = 'Buy now';
        button.disabled = !acceptedReview;
      }
    }
  });
  void loadReview();
}

async function startCheckout(event) {
  if (themeConfig.inquiryMode) {
    showMessage('Please contact us to confirm availability and final price before payment.');
    return;
  }
  const button = event.currentTarget;
  button.disabled = true;
  showMessage('');
  try {
    if (!themeConfig.cartEnabled) {
      showMessage(themeConfig.testMode && themeConfig.embeddedCatalogUrl
        ? 'Checkout is disabled while historical inventory is being tested.'
        : 'Open this page in the Shopify Development Store to test checkout.');
      return;
    }
    await runShopifyCheckout({
      api,
      selection: buildQuoteInput(model.selection),
      idempotencyKey: checkoutIdempotencyKey(model.selection),
      cartEnabled: themeConfig.cartEnabled,
      onProgress: showMessage,
      navigate: (path) => window.location.assign(path),
    });
  } catch (error) {
    showMessage(errorMessage(error));
  } finally {
    button.disabled = false;
  }
}

async function requestQuote() {
  showMessage('');
  try {
    model.quote = await api.postQuote(buildQuoteInput(model.selection));
    if (!canReviewCurrentSelection()) {
      model.quote = null;
      renderComplete();
      renderSummary();
      showStep(1);
      showMessage(themeConfig.inquiryMode
        ? 'The selected diamond could not be found in this catalog. Please reload your selection.'
        : 'The quote could not be verified against current inventory. Reload the selection and try again.');
      return;
    }
    renderComplete();
    renderSummary();
    showStep(2);
  } catch (error) {
    showMessage(errorMessage(error));
  }
}

function summaryRow(label, value) {
  const row = element('div');
  const valueNode = element('dd');
  if (value instanceof Node) valueNode.append(value);
  else valueNode.textContent = value;
  row.append(element('dt', null, label), valueNode);
  return row;
}

function renderSummary() {
  const diamond = model.selectedDiamond;
  ui.summaryHeading.textContent = 'Your loose diamond';
  ui.summaryVisual.replaceChildren();
  if (diamond) {
    ui.summaryVisual.append(safeImage(diamond.image_url, '', null));
  } else {
    const emptyState = element('div', 'summary-empty-state');
    emptyState.append(
      safeImage(EMPTY_DIAMOND_IMAGE, '', 'summary-empty-image'),
      element('span', null, 'Choose a diamond to preview it here'),
    );
    ui.summaryVisual.append(emptyState);
  }
  const rows = [
    summaryRow('Diamond', diamond
      ? `${diamond.carat.toFixed(2)} ct ${diamond.shape}, ${diamond.color}/${diamond.clarity}`
      : fallbackDiamondLabel()),
    summaryRow('Purchase', 'Loose diamond'),
  ];
  if (diamond) {
    rows.push(
      summaryRow('Certificate lab', diamond.lab),
      summaryRow('Certificate no.', diamond.cert_no),
      summaryRow('Availability', availabilityText(diamond)),
    );
  }
  if (diamond?.certificate_url) {
    rows.push(summaryRow('Verification', verificationLink(diamond.certificate_url, 'certificate-link', diamond.cert_no)));
  }
  ui.summaryDetails.replaceChildren(...rows);
  document.querySelector('#summary-total-label').textContent = themeConfig.inquiryMode ? 'Reference price (USD)' : 'Estimated total';
  document.querySelector('#summary-price-note').textContent = themeConfig.inquiryMode ? checkoutPriceNote : 'Tax calculated later in Shopify checkout';
  ui.summaryTotal.textContent = model.quote ? money.format(model.quote.total_price) : '—';
  updateWhatsAppHelp(diamond);
  updateEmailHelp(diamond);
  refreshIcons();
}

function stepThumbSource(step) {
  const diamond = model.selectedDiamond;
  if (step === 1) return diamond ? [diamond.image_url, `${diamond.shape} diamond`] : null;
  if (step !== 2) return null;
  return diamond ? [diamond.image_url, 'Loose diamond'] : null;
}

function renderProgressThumbs() {
  for (const item of ui.progressItems) {
    const step = Number(item.dataset.progressStep);
    const slot = item.querySelector('[data-step-thumb]');
    if (!slot) continue;
    slot.replaceChildren();
    const source = step < model.step ? stepThumbSource(step) : null;
    if (source) slot.append(safeImage(source[0], source[1]));
    slot.hidden = !source;
  }
  ui.startOver.hidden = !model.selection.diamond_id;
}

function startOver() {
  diamondSelectionVersion += 1;
  Object.assign(model, clearDiamondState(model.selection));
  writeSelection(model.selection);
  ui.completeRing.replaceChildren();
  for (const dialog of [ui.filterDialog, ui.diamondDrawer, ui.compareDialog]) {
    if (dialog.open) dialog.close();
  }
  showMessage('');
  showStep(1);
  renderDiamonds();
}

function showStep(step) {
  if (step !== 2) nativeReviewVersion += 1;
  if (step === 2 && !model.selection.diamond_id) step = 1;
  const reviewEnabled = canReviewCurrentSelection();
  if (step === 2 && !reviewEnabled) step = model.selection.diamond_id ? 1 : 1;
  model.step = step;
  for (const [index, panel] of ui.stepPanels.entries()) {
    panel.hidden = index + 1 !== step;
  }
  for (const item of ui.progressItems) {
    const itemStep = Number(item.dataset.progressStep);
    item.classList.toggle('is-current', itemStep === step);
    item.classList.toggle('is-complete', itemStep < step);
    const button = item.querySelector('button');
    button.disabled = itemStep === 2 && !reviewEnabled;
  }
  ui.mobileToolbar.hidden = step !== 1;
  renderProgressThumbs();
  if (step === 2) renderComplete();
  renderSummary();
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function openDiamondDrawer(diamond) {
  ui.drawerContent.replaceChildren();
  const image = safeImage(
    diamond.image_url,
    `${diamond.shape} ${diamond.carat} carat diamond`,
    'drawer-image',
  );
  const title = element('div', 'drawer-title-row');
  const titleCopy = element('div');
  titleCopy.append(
    element('h3', null, `${diamond.carat.toFixed(2)} ct ${diamond.shape}`),
    element('p', null, `${diamond.color} color · ${diamond.clarity} clarity · ${diamond.lab}`),
  );
  const drawerPrice = element('div');
  drawerPrice.append(element('strong', 'drawer-price', money.format(diamond.price_usd)));
  if (themeConfig.inquiryMode) drawerPrice.append(element('p', 'complete-note', 'Reference price (USD)'));
  title.append(titleCopy, drawerPrice);
  const details = element('dl', 'detail-grid');
  const entries = [
    ['Certificate lab', diamond.lab],
    ['Certificate no.', diamond.cert_no],
    ['Cut', gradedValue(diamond.cut)],
    ['Polish', gradedValue(diamond.polish)],
    ['Symmetry', gradedValue(diamond.symmetry)],
    ['Fluorescence', gradedValue(diamond.fluorescence)],
    ['Measurements', diamond.measurements],
    ['Table', gradedValue(diamond.table_pct, '%')],
    ['Depth', gradedValue(diamond.depth_pct, '%')],
    ['L/W ratio', diamond.lw_ratio === null ? '—' : diamond.lw_ratio.toFixed(2)],
    [themeConfig.inquiryMode ? 'Listed location (confirm)' : 'Ships from', locationLabel(diamond.location)],
    ['Availability', availabilityText(diamond)],
  ];
  for (const [label, value] of entries) {
    details.append(summaryRow(label, value));
  }
  const actions = element('div', 'drawer-actions');
  const select = element('button', 'button button-primary', 'Select diamond');
  select.type = 'button';
  select.addEventListener('click', () => chooseDiamond(diamond));
  actions.append(select);
  const drawerVerifyLink = verificationLink(diamond.certificate_url, 'button button-secondary', diamond.cert_no);
  if (drawerVerifyLink) actions.append(drawerVerifyLink);
  if (diamond.video_url) {
    const video = element('a', 'button button-secondary', 'View video');
    video.href = diamond.video_url;
    video.target = '_blank';
    video.rel = 'noopener noreferrer';
    actions.append(video);
  }
  const mediaRequest = element(
    'a',
    'button button-secondary media-request-link',
    themeConfig.inquiryMode ? 'Check availability on WhatsApp' : diamond.video_url ? 'Request more photos' : 'Request photos or video',
  );
  mediaRequest.href = whatsappUrl(diamond);
  mediaRequest.target = '_blank';
  mediaRequest.rel = 'noopener noreferrer';
  mediaRequest.setAttribute(
    'aria-label',
    themeConfig.inquiryMode
      ? 'Check diamond availability, final price and photos on WhatsApp'
      : `Ask Julysnow on WhatsApp for additional photos or video of ${diamond.carat.toFixed(2)} carat ${diamond.shape} diamond`,
  );
  mediaRequest.prepend(icon('message-circle'));
  actions.append(mediaRequest);
  const emailRequest = element('a', 'button button-secondary media-request-link email-request-link', 'Email us');
  emailRequest.href = emailUrl(diamond);
  emailRequest.setAttribute(
    'aria-label',
    themeConfig.inquiryMode
      ? 'Email Julysnow to confirm diamond availability, final price and photos'
      : `Email Julysnow for additional photos or video of ${diamond.carat.toFixed(2)} carat ${diamond.shape} diamond`,
  );
  emailRequest.prepend(icon('mail'));
  actions.append(emailRequest);
  const hasSpecificImage = typeof diamond.image_url === 'string'
    && !diamond.image_url.includes('/placeholders/');
  const mediaNote = element(
    'p',
    'media-note',
    diamond.video_url
      ? 'Want another angle or a daylight photo? We can share more on request.'
      : hasSpecificImage
        ? 'Ask us whether additional photos or a video are available.'
        : 'This listing is shown with a shape preview. Ask us for a private photo or video.',
  );
  ui.drawerContent.append(image, title, details, mediaNote);
  if (themeConfig.inquiryMode) {
    ui.drawerContent.append(element('p', 'complete-note', `${checkoutPriceNote} Catalog date: ${catalogDateText(diamond.synced_at || model.meta?.synced_at)}.`));
  }
  ui.drawerContent.append(actions);
  document.querySelector('#drawer-title').textContent = `${diamond.shape} diamond`;
  ui.diamondDrawer.showModal();
}

function collectFilters(form) {
  return Object.fromEntries(
    [...form.querySelectorAll('[data-filter]')]
      .map((control) => [control.dataset.filter, control.value.trim()])
      .filter(([, value]) => value !== ''),
  );
}

function copyFilters(source, destination) {
  for (const target of destination.querySelectorAll('[data-filter]')) {
    const sourceControl = source.querySelector(`[data-filter="${target.dataset.filter}"]`);
    target.value = sourceControl?.value ?? '';
  }
  syncFilterButtons(destination);
}

function syncFilterButtons(form) {
  for (const button of form.querySelectorAll('.shape-button')) {
    button.setAttribute('aria-pressed', String(button.dataset.shape === (form.querySelector('[data-filter="shape"]')?.value ?? '')));
  }
  for (const button of form.querySelectorAll('[data-filter-choice]')) {
    const value = form.querySelector('[data-filter="' + button.dataset.filterChoice + '"]')?.value ?? '';
    button.setAttribute('aria-pressed', String(value === button.dataset.filterValue));
  }
  const shapeValue = form.querySelector('.mobile-shape-value');
  if (shapeValue) shapeValue.textContent = form.querySelector('[data-filter="shape"]')?.value || 'All shapes';
}

function mobileRange(key, title) {
  const original = ui.desktopFilters.querySelector('[data-filter="' + key + '_min"]').closest('fieldset');
  const field = element('fieldset', 'mobile-range');
  field.append(element('legend', null, title));
  const inputs = element('div', 'mobile-range-inputs');
  for (const [suffix, labelText] of [['min', 'Minimum'], ['max', 'Maximum']]) {
    const label = element('label');
    const input = original.querySelector('[data-filter="' + key + '_' + suffix + '"]').cloneNode(true);
    input.setAttribute('aria-label', labelText + ' ' + (key === 'carat' ? 'carat' : 'price'));
    input.inputMode = key === 'carat' ? 'decimal' : 'numeric';
    input.placeholder = 'Any';
    label.append(element('span', 'mobile-input-caption', labelText), input);
    inputs.append(label);
  }
  field.append(inputs);
  return field;
}

function mobileChoices(key, title) {
  const select = ui.desktopFilters.querySelector('[data-filter="' + key + '"]').cloneNode(true);
  select.hidden = true;
  const field = element('fieldset', 'mobile-choice-field');
  const buttons = element('div', 'mobile-choice-grid');
  buttons.setAttribute('role', 'group');
  buttons.setAttribute('aria-label', title + ' grade');
  for (const option of select.options) {
    const button = element('button', 'mobile-choice', option.value || 'Any');
    button.type = 'button';
    button.dataset.filterChoice = key;
    button.dataset.filterValue = option.value;
    button.addEventListener('click', () => {
      select.value = option.value;
      syncFilterButtons(ui.mobileFilters);
      queueMobileResultCount();
    });
    buttons.append(button);
  }
  field.append(element('legend', null, title), buttons, select);
  return field;
}

function buildMobileFilters() {
  const body = ui.mobileFilters.querySelector('.filter-dialog-body');
  const shape = element('details', 'mobile-filter-section mobile-shape-section');
  const shapeSummary = element('summary');
  shapeSummary.append(element('span', null, 'Shape'), element('span', 'mobile-shape-value', 'All shapes'), icon('chevron-down'));
  shape.append(shapeSummary, ui.desktopFilters.querySelector('.shape-selector').cloneNode(true));

  const size = element('section', 'mobile-filter-section');
  size.append(element('h3', null, 'Size & budget'), mobileRange('carat', 'Carat weight'), mobileRange('price', 'Price (USD)'));
  const quality = element('section', 'mobile-filter-section');
  quality.append(element('h3', null, 'Color & clarity'), mobileChoices('color', 'Color'), mobileChoices('clarity', 'Clarity'));

  const advanced = element('details', 'mobile-filter-section mobile-advanced-section');
  const advancedSummary = element('summary');
  advancedSummary.append(element('span', null, 'Cut, polish & fluorescence'), icon('chevron-down'));
  const advancedContent = element('div', 'mobile-advanced-content');
  for (const key of ['cut', 'lab']) advancedContent.append(ui.desktopFilters.querySelector('[data-filter="' + key + '"]').closest('label').cloneNode(true));
  for (const child of ui.desktopFilters.querySelector('.advanced-grid').children) advancedContent.append(child.cloneNode(true));
  advanced.append(advancedSummary, advancedContent);
  body.replaceChildren(shape, size, quality, advanced);
  syncFilterButtons(ui.mobileFilters);
}

let mobileCountTimer;
let mobileCountRequest = 0;
function queueMobileResultCount() {
  clearTimeout(mobileCountTimer);
  const request = ++mobileCountRequest;
  const submit = ui.mobileFilters.querySelector('[type="submit"]');
  submit.textContent = 'Show results';
  mobileCountTimer = setTimeout(async () => {
    try {
      const response = await api.getDiamonds({ ...collectFilters(ui.mobileFilters), page: 1, page_size: 1 });
      if (request === mobileCountRequest) submit.textContent = 'Show ' + response.meta.total.toLocaleString('en-US') + ' diamonds';
    } catch {
      if (request === mobileCountRequest) submit.textContent = 'Show results';
    }
  }, 180);
}

function renderActiveFilters() {
  const target = document.querySelector('#active-filters');
  const chips = [];
  for (const [key, title] of [['shape', 'Shape'], ['color', 'Color'], ['clarity', 'Clarity'], ['cut', 'Cut'], ['lab', 'Lab'], ['polish', 'Polish'], ['symmetry', 'Symmetry'], ['fluorescence', 'Fluorescence']]) {
    if (model.filters[key]) chips.push({ keys: [key], text: title + ': ' + model.filters[key] });
  }
  for (const [key, title] of [['carat', 'Carat'], ['price', 'Price'], ['lw', 'L/W'], ['table', 'Table %'], ['depth', 'Depth %']]) {
    const min = model.filters[key + '_min'];
    const max = model.filters[key + '_max'];
    if (min || max) chips.push({ keys: [key + '_min', key + '_max'], text: title + ': ' + (min || 'Any') + ' – ' + (max || 'Any') });
  }
  target.replaceChildren();
  target.hidden = chips.length === 0;
  for (const chip of chips) {
    const button = element('button', 'active-filter-chip');
    button.type = 'button';
    button.setAttribute('aria-label', 'Remove ' + chip.text);
    button.append(element('span', null, chip.text), icon('x'));
    button.addEventListener('click', () => {
      for (const key of chip.keys) ui.desktopFilters.querySelector('[data-filter="' + key + '"]').value = '';
      applyFilters(ui.desktopFilters, ui.mobileFilters);
    });
    target.append(button);
  }
  const count = document.querySelector('#mobile-filter-count');
  count.textContent = String(chips.length);
  count.hidden = chips.length === 0;
  refreshIcons();
}

function applyFilters(form, destination) {
  if (destination) copyFilters(form, destination);
  model.filters = collectFilters(form);
  syncFilterButtons(form);
  renderActiveFilters();
  model.page = 1;
  loadDiamonds();
}

function bindPageLifecycle() {
  window.addEventListener('pagehide', () => {
    // Ignore checks started before leaving, including requests resumed from the browser cache.
    diamondSelectionVersion += 1;
    nativeReviewVersion += 1;
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) startOver();
  });
}

function bindEvents() {
  ui.startOver.addEventListener('click', startOver);
  for (const button of document.querySelectorAll('[data-go-step]')) {
    button.addEventListener('click', async () => {
      const step = Number(button.dataset.goStep);
      showStep(step);
    });
  }
  for (const button of document.querySelectorAll('[data-close-dialog]')) {
    button.addEventListener('click', () => {
      document.querySelector(`#${button.dataset.closeDialog}`).close();
    });
  }
  document.querySelector('#open-filters').addEventListener('click', () => {
    copyFilters(ui.desktopFilters, ui.mobileFilters);
    syncFilterButtons(ui.mobileFilters);
    ui.filterDialog.showModal();
    ui.mobileFilters.querySelector('.filter-dialog-body').scrollTop = 0;
    queueMobileResultCount();
  });
  ui.desktopFilters.addEventListener('submit', (event) => {
    event.preventDefault();
    applyFilters(ui.desktopFilters, ui.mobileFilters);
  });
  ui.desktopFilters.addEventListener('reset', () => {
    setTimeout(() => {
      for (const input of ui.desktopFilters.querySelectorAll('[data-filter]')) input.value = '';
      applyFilters(ui.desktopFilters, ui.mobileFilters);
    }, 0);
  });

  // Shape selector buttons
  document.querySelectorAll('.shape-button').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const shape = button.getAttribute('data-shape');
      if (button.closest('#mobile-filters')) {
        ui.mobileFilters.querySelector('[data-filter="shape"]').value = shape;
        syncFilterButtons(ui.mobileFilters);
        queueMobileResultCount();
      } else {
        ui.desktopFilters.querySelector('[data-filter="shape"]').value = shape;
        applyFilters(ui.desktopFilters, ui.mobileFilters);
      }
    });
  });

  ui.mobileFilters.addEventListener('input', queueMobileResultCount);
  ui.mobileFilters.addEventListener('change', queueMobileResultCount);
  ui.mobileFilters.addEventListener('submit', (event) => {
    event.preventDefault();
    applyFilters(ui.mobileFilters, ui.desktopFilters);
    ui.filterDialog.close();
    document.querySelector('.results-toolbar').scrollIntoView({ block: 'start' });
  });
  ui.mobileFilters.addEventListener('reset', () => {
    setTimeout(() => {
      for (const input of ui.mobileFilters.querySelectorAll('[data-filter]')) input.value = '';
      syncFilterButtons(ui.mobileFilters);
      queueMobileResultCount();
    }, 0);
  });
  ui.sort.addEventListener('change', () => {
    model.sort = ui.sort.value;
    ui.mobileSort.value = model.sort;
    model.page = 1;
    loadDiamonds();
  });
  ui.mobileSort.addEventListener('change', () => {
    model.sort = ui.mobileSort.value;
    ui.sort.value = model.sort;
    model.page = 1;
    loadDiamonds();
  });
  ui.compareOpen.addEventListener('click', openCompareDialog);
  ui.compareClear.addEventListener('click', () => {
    model.compare = [];
    renderCompareTray();
    renderDiamonds();
    if (ui.compareDialog.open) ui.compareDialog.close();
  });
  for (const dialog of [ui.filterDialog, ui.diamondDrawer, ui.compareDialog]) {
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  }
}

async function initialize() {
  document.querySelector('#price-column-label').textContent = themeConfig.inquiryMode ? 'Reference price' : 'Price';
  document.querySelector('#delivery-column-label').textContent = themeConfig.inquiryMode ? 'Availability' : 'Delivery';
  if (themeConfig.inquiryMode) {
    document.querySelector('.diamond-table').classList.add('inquiry-prices');
    document.querySelector('#whatsapp-help strong').textContent = 'Check availability';
    document.querySelector('#whatsapp-help small').textContent = `Confirm price, delivery & photos on WhatsApp · ${WHATSAPP_DISPLAY}`;
    document.querySelector('#whatsapp-help').setAttribute('aria-label', 'Check availability on WhatsApp');
    document.querySelector('#email-help').setAttribute('aria-label', 'Email Julysnow to confirm availability and final price');
  }
  buildMobileFilters();
  bindEvents();
  bindPageLifecycle();
  startOver();
  refreshIcons();
  await loadDiamonds();
}

initialize().catch((error) => showMessage(errorMessage(error)));
